/**
 * FeedbackModal — lets any authenticated user submit a feature request or feedback.
 *
 * Captures PostHog session ID and current URL for context linking.
 * Fires a `customer_feedback` event on the frontend (backend fires one too as belt-and-suspenders).
 *
 * @spec SPEC:FEATURE:FEEDBACK
 */
import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import posthog from 'posthog-js'
import { Button } from '@/components/ui/button'
import { useSubmitFeedback } from '@/queries/useFeedback'
import { events } from '@/services/events'
import { logger } from '@/services/logger'

interface FeedbackModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
  const [request, setRequest] = useState('')
  const [success, setSuccess] = useState(false)
  const submitFeedback = useSubmitFeedback()

  const handleSubmit = async () => {
    if (!request.trim()) return

    try {
      const posthogSessionId = posthog.get_session_id?.() ?? null
      const currentUrl = window.location.href

      await submitFeedback.mutateAsync({
        request: request.trim(),
        posthogSessionId,
        currentUrl,
      })

      events.track('customer_feedback', {
        request: request.trim(),
        posthogSessionId,
        currentUrl,
      })

      setRequest('')
      setSuccess(true)
      setTimeout(() => {
        setSuccess(false)
        onOpenChange(false)
      }, 1500)
    } catch (err) {
      logger.error('feedback.submit_failed', err)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setRequest('')
      setSuccess(false)
      submitFeedback.reset()
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] rounded-lg border border-border bg-background p-6 shadow-lg focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Share feedback
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-muted-foreground">
            Tell us what you think, report a bug, or request a feature.
          </Dialog.Description>

          {success ? (
            <div className="mt-6 rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3">
              <p className="text-sm font-medium text-green-700 dark:text-green-400">
                Thanks for your feedback!
              </p>
            </div>
          ) : (
            <>
              {submitFeedback.isError && (
                <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3">
                  <p className="text-sm font-medium text-destructive">
                    {submitFeedback.error?.message ?? 'Failed to submit feedback'}
                  </p>
                </div>
              )}

              <textarea
                value={request}
                onChange={(e) => setRequest(e.target.value)}
                placeholder="What's on your mind?"
                maxLength={5000}
                rows={5}
                className="mt-4 w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />

              <div className="mt-1 text-right text-xs text-muted-foreground">
                {request.length}/5000
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <Dialog.Close asChild>
                  <Button variant="outline" size="sm">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  size="sm"
                  disabled={!request.trim() || submitFeedback.isPending}
                  onClick={() => void handleSubmit()}
                >
                  {submitFeedback.isPending ? 'Sending...' : 'Submit'}
                </Button>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
